import { CellContext, createColumnHelper, HeaderContext } from '@tanstack/react-table';

import { Text } from '@chakra-ui/react';

import { format, parseISO } from 'date-fns';

import {
  Badge,
  Currency,
  DEFAULT_DATE_FORMAT,
  TimesheetEarningsStatus,
} from '@aslanpay/portal-common-lib';

import { EarningsColumns } from 'app/constants/table';
import { EarningsStatusDetails } from 'app/constants/earnings';
import { EarningsTableRow } from 'app/components/Individuals/IndividualProfile/IndividualEarningsTab/types';
import { formatCurrency } from 'app/utils/strings';

function getEarningsColumns(
  currency: Currency = 'GBP',
  onSelect?: (row: EarningsTableRow) => void,
  selectedKey?: string
) {
  const columnHelper = createColumnHelper<EarningsTableRow>();

  const columns = Object.keys(EarningsColumns).map((field, index) => {
    return columnHelper.accessor(field as keyof EarningsTableRow, {
      cell: ({ getValue }) => {
        const value = getValue();

        switch (field as keyof EarningsTableRow) {
          case 'date':
            return (
              <Text>{value ? format(parseISO(value as string), DEFAULT_DATE_FORMAT) : '-'}</Text>
            );
          case 'grossEarnings':
          case 'netEarnings':
            return (
              <Text textAlign="right">
                {formatCurrency(typeof value === 'number' ? value : 0, currency, 'en-GB')}
              </Text>
            );
          case 'status': {
            if (!value) return <Text>-</Text>;
            const details = EarningsStatusDetails[value as TimesheetEarningsStatus];
            return <Badge value={details.label} status={details.badgeStatus} />;
          }
          default:
            return value;
        }
      },
      header: EarningsColumns[field as keyof typeof EarningsColumns],
      enableSorting: true,
      meta: {
        getHeaderContext: (context: HeaderContext<EarningsTableRow, unknown>) => {
          if (['grossEarnings', 'netEarnings'].includes(context.header.id)) {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
        getCellContext: (context: CellContext<EarningsTableRow, unknown>) => {
          if (!onSelect) return undefined;

          const isSelected = selectedKey === context.row.original.key;
          const isFirstColumn = index === 0;
          return {
            onClick: () => onSelect(context.row.original),
            style: {
              cursor: 'pointer',
              ...(isSelected && {
                backgroundColor: 'var(--chakra-colors-purple-50)',
                ...(isFirstColumn && {
                  borderLeft: '3px solid var(--chakra-colors-brand-purple)',
                }),
              }),
            },
          };
        },
      },
    });
  });

  return columns;
}

export default getEarningsColumns;
